'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { HOME_SLIDE } from '@/components/Mobile/Home/slides';

type ShowreelSound = 'on' | 'fadingOut' | 'off';

const SHOWREEL_FADE_OUT_MS = 1700;

interface MobileShellContextValue {
  slide: number;
  setSlide: (slide: number) => void;
  directoryOpen: boolean;
  openDirectory: () => void;
  closeDirectory: () => void;
  isShowreelMuted: boolean;
  toggleShowreelMuted: () => void;
  fadeOutShowreel: () => void;
  setShowreelVideo: (video: HTMLVideoElement | null) => void;
}

const MobileShellContext = createContext<MobileShellContextValue>({
  slide: HOME_SLIDE.title,
  setSlide: () => {},
  directoryOpen: false,
  openDirectory: () => {},
  closeDirectory: () => {},
  isShowreelMuted: true,
  toggleShowreelMuted: () => {},
  fadeOutShowreel: () => {},
  setShowreelVideo: () => {},
});

export const MobileShellProvider = ({ children }: { children: ReactNode }) => {
  const [slide, setSlide] = useState<number>(HOME_SLIDE.title);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [showreelSound, setShowreelSound] = useState<ShowreelSound>('off');
  const [showreelVideo, setShowreelVideoElement] = useState<HTMLVideoElement | null>(null);

  const openDirectory = useCallback(() => setDirectoryOpen(true), []);
  const closeDirectory = useCallback(() => setDirectoryOpen(false), []);

  const toggleShowreelMuted = useCallback(() => {
    setShowreelSound((sound) => (sound === 'on' ? 'off' : 'on'));
  }, []);

  const fadeOutShowreel = useCallback(() => {
    setShowreelSound((sound) => (sound === 'on' ? 'fadingOut' : sound));
  }, []);

  const setShowreelVideo = useCallback((video: HTMLVideoElement | null) => {
    setShowreelVideoElement(video);
    if (!video) setShowreelSound('off');
  }, []);

  useEffect(() => {
    if (!showreelVideo) return;

    if (showreelSound === 'on') {
      showreelVideo.volume = 1;
      showreelVideo.muted = false;
      return;
    }

    if (showreelSound === 'off') {
      showreelVideo.muted = true;
      return;
    }

    const startVolume = showreelVideo.volume;
    const fadeStartedAt = performance.now();
    let frameId = 0;

    const stepFade = (now: number) => {
      const progress = Math.min(Math.max((now - fadeStartedAt) / SHOWREEL_FADE_OUT_MS, 0), 1);
      showreelVideo.volume = startVolume * (1 - progress);

      if (progress < 1) {
        frameId = requestAnimationFrame(stepFade);
        return;
      }

      setShowreelSound('off');
    };

    frameId = requestAnimationFrame(stepFade);

    return () => cancelAnimationFrame(frameId);
  }, [showreelVideo, showreelSound]);

  return (
    <MobileShellContext.Provider
      value={{
        slide,
        setSlide,
        directoryOpen,
        openDirectory,
        closeDirectory,
        isShowreelMuted: showreelSound !== 'on',
        toggleShowreelMuted,
        fadeOutShowreel,
        setShowreelVideo,
      }}
    >
      {children}
    </MobileShellContext.Provider>
  );
};

export default MobileShellContext;
